import { useId, useState } from "react";
import type { MixComponent } from "../../types";

/**
 * One pigment jar with its drop count — the whole point of the result screen.
 *
 * When `component.jar` carries a real photo (frontend/public/kb/pigments/…) we
 * show it; until the photos arrive we draw the jar in the shade's own colour so
 * the screen already reads as "bottles", not as text.
 */
export default function PigmentJar({
  c, size = 66,
}: {
  c: MixComponent;
  size?: number;
}) {
  const [photoFailed, setPhotoFailed] = useState(false);
  const showPhoto = !!c.jar && !photoFailed;
  const h = Math.round(size * 1.28);

  return (
    <div style={{ width: size, flex: "none", textAlign: "center" }}>
      <div style={{ position: "relative", height: h, display: "grid", placeItems: "center" }}>
        {showPhoto ? (
          <img
            src={c.jar!}
            alt={c.name}
            onError={() => setPhotoFailed(true)}
            style={{ maxWidth: "100%", maxHeight: h, objectFit: "contain", filter: "drop-shadow(0 6px 12px rgba(0,0,0,0.35))" }}
          />
        ) : (
          <DrawnJar hex={c.hex} label={c.short} width={size} height={h} />
        )}
      </div>

      {/* Drops — the number the master actually counts. */}
      <div
        className="t-mono"
        style={{
          marginTop: 6, fontWeight: 800, fontSize: "1.05rem", lineHeight: 1.1,
          color: "var(--accent-text)",
        }}
      >
        {c.drops}
      </div>
      <div className="t-xs muted-2" style={{ lineHeight: 1.2 }}>кап.</div>
    </div>
  );
}

/** Generated jar: cap, body filled with the pigment, gloss, number on the label. */
function DrawnJar({ hex, label, width, height }: { hex: string; label: string; width: number; height: number }) {
  const long = label.length > 3;
  // Shade labels are «№6» / «Terra» — never safe inside url(#…), so use a
  // generated id instead, which also keeps every jar's gloss its own.
  const gid = `jar-gloss-${useId().replace(/:/g, "")}`;
  return (
    <svg width={width} height={height} viewBox="0 0 66 84" role="img" aria-label={label}>
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#fff" stopOpacity="0.34" />
          <stop offset="38%" stopColor="#fff" stopOpacity="0.04" />
          <stop offset="72%" stopColor="#000" stopOpacity="0.10" />
          <stop offset="100%" stopColor="#000" stopOpacity="0.24" />
        </linearGradient>
      </defs>

      {/* cap */}
      <rect x="20" y="2" width="26" height="13" rx="3.5" fill="#2b2b30" />
      <rect x="22.5" y="4" width="21" height="3" rx="1.5" fill="#fff" opacity="0.16" />
      {/* neck */}
      <rect x="24.5" y="14" width="17" height="6" fill="#3a3a41" />
      {/* body, filled with the pigment itself */}
      <rect x="8" y="19" width="50" height="63" rx="9" fill={hex} />
      <rect x="8" y="19" width="50" height="63" rx="9" fill={`url(#${gid})`} />
      <rect x="8" y="19" width="50" height="63" rx="9" fill="none" stroke="rgba(0,0,0,0.28)" strokeWidth="1" />
      {/* label band */}
      <rect x="12" y="43" width="42" height="24" rx="4" fill="#fbf8f3" opacity="0.94" />
      <text
        x="33" y="59" textAnchor="middle"
        fontSize={long ? 11 : 15} fontWeight="800" fill="#26211c"
        fontFamily="ui-sans-serif, system-ui, sans-serif"
      >
        {label}
      </text>
    </svg>
  );
}
